import React, { CSSProperties } from 'react';
import { Icons, SVG, SVGIcon } from '../../../components/atoms/SVG';
import { SpanProps } from '../../../types/HtmlProps';
import { PageType } from '../../../types/api/PageType';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import bem from '../../../lib/bemUtils/bem';

export type PageTypeIconModifiers = 'large-rounded' | 'x-large-round' | 'align-self-top' | 'template';

export type HEX = `#${string}`;

type colorType = string
  | 'charcoal'
  | 'burgundy'
  | 'vale'
  | 'brown'
  | 'green'
  | 'mint'
  | 'lightmint'
  | 'aqua-lake'
  | 'darkblue'
  | 'fadedblue'
  | 'violet-ink'
  | 'safety-orange'
  | 'persian-red'
  | 'fuzzy-wuzzy'
  | 'midnight-green'
  | 'deep-marine'
  | 'golden-foil'
  | 'gold-taffeta'
  | 'amethyst'
  | 'snow';

type IconProps = {
  icon: SVGIcon;
  color: colorType;
  modifier?: Modifier<PageTypeIconModifiers>;
  borderColor?: HEX;
};

type PageTypeIconProps = {
  pageType: PageType;
  borderColor?: HEX;
  modifier?: Modifier<PageTypeIconModifiers>;
};

type TemplateIconProps = {
  icon: SVGIcon;
  borderColor?: HEX;
};

type IconAndColor = { icon: SVGIcon; color: colorType };
const getIconAndColorType = (pageType: PageType): IconAndColor => {
  switch (pageType) {
    case PageType.teamroom:
      return { icon: Icons.TEAMROOM_PEOPLE, color: 'green' };
    case PageType.topichome:
    case PageType.topic:
      return { icon: Icons.TOPIC, color: 'charcoal' };
    case PageType.faqlist:
    case PageType.faq:
    case PageType.faqlistpost:
      return { icon: Icons.FAQ, color: 'brown' };
    case PageType.forum:
      return { icon: Icons.FORUM, color: 'persian-red' };
    case PageType.forumthread:
      return { icon: Icons.FORUM, color: 'fuzzy-wuzzy' };
    case PageType.orgunithome:
    case PageType.orgunit:
      return { icon: Icons.ORGANISATION, color: 'charcoal' };
    case PageType.poll:
    case PageType.survey:
      return { icon: Icons.SURVEY, color: 'safety-orange' };
    case PageType.event:
      return { icon: Icons.CALENDAR, color: 'lightmint' };
    case PageType.eventlist:
      return { icon: Icons.CALENDAR, color: 'mint' };
    case PageType.processhome:
    case PageType.process:
      return { icon: Icons.PROCESS2, color: 'charcoal' };
    case PageType.locationhome:
    case PageType.location:
      return { icon: Icons.LOCATION, color: 'charcoal' };
    case PageType.wikihome:
      return { icon: Icons.WIKI, color: 'burgundy' };
    case PageType.wiki:
      return { icon: Icons.WIKI, color: 'vale' };
    case PageType.blog:
      return { icon: Icons.BLOG, color: 'burgundy' };
    case PageType.blogpost:
      return { icon: Icons.BLOG, color: 'vale' };
    case PageType.pagelist:
    case PageType.pagelistteaser:
      return { icon: Icons.PAGE_LIST, color: 'darkblue' };
    case PageType.document:
      return { icon: Icons.PAGE_LIST, color: 'midnight-green' };
    case PageType.sharedfileshome:
      return { icon: Icons.FILES_SHARED, color: 'violet-ink' };
    case PageType.sharedfilesdirectory:
      return { icon: Icons.FILES_SHARED, color: 'amethyst' };
    case PageType.task:
      return { icon: Icons.TASK, color: 'charcoal' };
    case PageType.externallink:
    case PageType.externalapplication:
      return { icon: Icons.EXTERNAL_LINK_ARROW, color: 'fadedblue' };
    case PageType.imagegallery:
      return { icon: Icons.IMAGE_GALLERY, color: 'darkblue' };
    case PageType.structurediagram:
      return { icon: Icons.GRAPHIC_STRUCTURE, color: 'darkblue' };
    case PageType.formapplication:
    case PageType.sonformapplication:
      return { icon: Icons.FORM, color: 'gold-taffeta' };
    case PageType.ticketbooking:
      return { icon: Icons.TICKET_BOOKING, color: 'golden-foil' };
    case PageType.node:
      return { icon: Icons.BLANK_PAGE, color: 'darkblue' };
    case PageType.nodehome:
      return { icon: Icons.BLANK_PAGE, color: 'charcoal' };
    default:
      return { icon: Icons.BLANK_PAGE, color: 'darkblue' };
  }
};

function Icon({ icon, color, modifier, borderColor, ...spanProps }: IconProps & SpanProps) {
  const baseClass = 'page-type-icon__container';
  const modifierClass = bem(baseClass, modifier);
  const colorClass = `${baseClass}--${color}`;
  const cssClass = [modifierClass, colorClass].filter(Boolean);

  return (
    <span
      className={cssClass.join(' ')}
      style={borderColor ? { borderColor: borderColor } as CSSProperties : undefined}
      {...spanProps}
    >
      <SVG svgSpriteId={icon} svgClass="page-type-icon__icon" />
    </span>
  );
}

export function PageTypeIcon({ pageType, modifier, ...spanProps }: PageTypeIconProps & SpanProps) {
  const { icon, color } = getIconAndColorType(pageType);

  return <Icon icon={icon} color={color as colorType} modifier={modifier} {...spanProps} />;
}

export function TemplateIcon({ icon, borderColor, ...spanProps }: TemplateIconProps & SpanProps) {
  return <Icon icon={icon} modifier="template" color="snow" borderColor={borderColor} {...spanProps} />;
}
